import {
  Box,
  CircularProgress,
  Tooltip,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { scCacheService } from "../../utils/scCacheService";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import {
  downloadDocx,
  DownloadDocxOptions,
} from "../../../../../../shared/components/document/docx-downloader";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import ViewerModalPopup from "../../../../../../shared/components/modals/viewerModal";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import PdfViewer from "../../../../../../shared/components/preview/pdf-viewer";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { downloadPDF } from "../../../../../../shared/hooks/file-downloader";
import { useApiV4 } from "../../../../../../shared/hooks/useApiV4";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { useScStore } from "../../../../../../store/scStandard.store";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import { isStandardWithdrawn } from "../../utils/scValidation";
import { EditableTable, EditableColumnDef } from "../../../../../../shared/components/table/inline-edit-table";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { CustomSelect } from "../../../../../../shared/components/inputfields/form-elements";
import scClaimsMapping from "./sc-claims-mapping.json";

// Table row type for Component Information table
interface ComponentTableRow {
  code: string;
  description: string;
}

// Table row type for SC Product Claims Category table
interface ClaimsTableRow {
  claimKey: string;
  scProductClaimsCategory: string;
  certifiedContent: string;
  blendingAllowed: string;
  mixingAllowed: string;
}

// Define the interface for API Response
export interface ComponentDetailsResponse {
  scId: string;
  componentId: string;
  scProductComponentCode: string;
  scComponentDetail: string;
  scProductDetailCode: string;
}

export interface ComponentDetailsApiResponse {
  scId: string;
  scProductComponentCode: string[];
}

export interface ProductClaimsResponse {
  scId: string;
  scProductClaimsCategory: string[];
}

interface ComponentClaimsCategoryDetailsTabProps {
  scStatus?: boolean;
}

const ComponentClaimsCategoryDetailsTab: React.FC<
  ComponentClaimsCategoryDetailsTabProps
> = ({ scStatus }) => {
  const navigate = useNavigate();
  const { SC_ID, formtype } = useParams();
  // useApiV4: V4-only hook that returns the 422 payload (needed for cross-tab validation on submit)
  const { executePost, loading: isSaving } = useApiV4();
  // useApi: shared hook for GETs and DELETEs — no 422 return needed here
  const { executeGet, executeDelete, loading: isLoading } = useApi();

  // State for component information table
  const [componentData, setComponentData] = useState<string[]>([]);
  const [claimsData, setClaimsData] = useState<string[]>([]);
  const [componentCodeOptions, setComponentCodeOptions] = useState<any[]>([]);
  const [scClaimsCodeOptions, setScClaimsCodeOptions] = useState<any[]>([]);
  // Read SC standards + PR0035 flag from the shared Zustand store (populated by the SC Details tab).
  // hasPR0035 is used as a fallback in submit pre-validation when fresh facility fetch returns empty.
  const { scStandards, hasPR0035, tabErrors, backendValidationErrors, scData, savedClaims } = useScStore();
  const { setTabError, setSavedClaims } = useScStore();

  // State for claims categories form
  const [claimsCategoryData, setClaimsCategoryData] = useState<string[]>([]);

  // Keep track of saved component details loaded from/saved to DB
  const [savedComponentData, setSavedComponentData] = useState<string[]>([]);

  // ── Table-UI state ───────────────────────────────────────────────────────
  // Component Information table
  const [componentTableData, setComponentTableData] = useState<ComponentTableRow[]>([]);
  const [componentEditingRowIndex, setComponentEditingRowIndex] = useState<number>(-1);
  const [componentDeleteDialogOpen, setComponentDeleteDialogOpen] = useState(false);
  const [componentDeletingRowIndex, setComponentDeletingRowIndex] = useState<number>(-1);

  // Claims Category table
  const [claimsTableData, setClaimsTableData] = useState<ClaimsTableRow[]>([]);
  const [claimsEditingRowIndex, setClaimsEditingRowIndex] = useState<number>(-1);
  const [claimsDeleteDialogOpen, setClaimsDeleteDialogOpen] = useState(false);
  const [claimsDeletingRowIndex, setClaimsDeletingRowIndex] = useState<number>(-1);

  // True while ALL initial data fetches are in-flight — hides stale content until ready
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Button states
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { isRestrictPopUpSave, setRestrictPopUpSave, cbLicensingCode } =
    useTransactionCertificateStore();
  const { notify } = useNotification();

  const hasSubmitSC: boolean = formtype === "submited-sc";
  const isReadOnlyBasedOnStatus = hasSubmitSC || scStatus;

  // Dynamically compute unsaved changes by comparing current UI state with last saved state.
  const hasUnsavedChanges = useMemo(() => {
    return (
      !_.isEqual([...claimsCategoryData].sort(), [...savedClaims].sort()) ||
      !_.isEqual([...componentData].sort(), [...savedComponentData].sort())
    );
  }, [claimsCategoryData, savedClaims, componentData, savedComponentData]);

  // Fetch standards only when the Zustand store is empty (edge case: user lands
  // directly on this tab without visiting SC Details tab first).
  const fetchStandardsFromSCDetails = async () => {
    if (!SC_ID) return;
    // Store already populated by SC Details tab — skip the network call
    if (useScStore.getState().scStandards.length > 0) return;
    try {
      const result = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
      const standards = result?.data?.scStandardInformation
        ?.filter((item: any) => !isStandardWithdrawn(item))
        ?.map((item: any) => item.scStandard) || [];
      useScStore.getState().setScStandards(standards);
    } catch (error) {
      console.error("Error fetching SC standards:", error);
      notify("Error fetching standards", "error");
    }
  };

  const loadInitialValues = async() => {
    setIsInitialLoading(true);
    try {
      await getComponentDetails();
      await getProductClaims();
      await getComponentCodes();
      await getSCClaimsCodes();
      await fetchStandardsFromSCDetails();
    } finally {
      setIsInitialLoading(false);
    }
  }

  // Fetch data on page load
  useEffect(() => {
    loadInitialValues();
  }, [SC_ID]);

  // Sync component table rows whenever componentData or options change (outside of editing).
  useEffect(() => {
    const rows: ComponentTableRow[] = componentData.map(code => ({
      code,
      description: code === 'na' ? 'na'
        : (componentCodeOptions.find((o: any) => o.value === code)?.label?.replace(/\s*\([^)]+\)\s*$/, '').trim() ?? ''),
    }));
    setComponentTableData(rows);
  }, [componentData, componentCodeOptions]);

  // Sync claims table rows whenever claimsCategoryData changes.
  useEffect(() => {
    const rows: ClaimsTableRow[] = claimsCategoryData.map(key => {
      const mapping = (scClaimsMapping as any)[key];
      return {
        claimKey: key,
        scProductClaimsCategory: mapping?.scProductClaimsCategory ?? key,
        certifiedContent: mapping?.certifiedContent ?? '',
        blendingAllowed: mapping?.blendingAllowed ?? '',
        mixingAllowed: mapping?.mixingAllowed ?? '',
      };
    });
    setClaimsTableData(rows);
  }, [claimsCategoryData]);

  // Fetch existing component details from API
  const getComponentDetails = async () => {
    if (!SC_ID) return;

    try {
      const response = await executeGet(API_ROUTES.GET_COMPONENT_DETAILS_V4, {
        scId: SC_ID,
      });

      if (response?.status === 200 && response.data) {
        const apiData: any = response.data;

        if (apiData.scProductComponentCode) {
          // Transform API response to component data format
          const transformedData: string[] = apiData?.scProductComponentCode;
          const cleanedComponents = transformedData.map((item) => item.split(";")[0].trim());
          setComponentData(cleanedComponents);
          setSavedComponentData(cleanedComponents);
        }
      }
    } catch (error) {
      console.error("Error fetching component details:", error);
      notify("Error fetching component details", "error");
    }
  };

  // Fetch existing product claims from API
  const getProductClaims = async () => {
    if (!SC_ID) return;

    try {
      const response = await executeGet(
        API_ROUTES.GET_PRODUCT_CLAIMS_DETAILS_V4,
        { scId: SC_ID }
      );

      if (response?.status === 200 && response.data) {
        const apiData: ProductClaimsResponse = response.data;

        // Only pre-populate if the API returned a non-empty array.
        // An empty or missing value means no claims were explicitly saved
        // and the dropdown should start empty (user must select).
        if (Array.isArray(apiData.scProductClaimsCategory) && apiData.scProductClaimsCategory.length > 0) {
          const cleanedClaims = apiData.scProductClaimsCategory.map(c => 
            c.replace(/&lt;/g, "<")
             .replace(/&gt;/g, ">")
             .replace(/&amp;/g, "&")
          );
          setClaimsCategoryData(cleanedClaims);
          // Mirror to Zustand so the parent stepper can proactively detect
          // missing-claims errors without the user needing to visit this tab first.
          setSavedClaims(cleanedClaims);
        }
      }
    } catch (error) {
      console.error("Error fetching product claims:", error);
      notify("Error fetching product claims", "error");
    }
  };

  // Fetch component codes from API
  const getComponentCodes = async () => {
    try {
      const queryParams = {
        query_param: TCAppConfig.GET_PRODUCT_DETAIL_CODE,
      };
      const response = await executeGet(
        TCAppConfig.GET_DTRACKIT_REF_CODE_V4,
        queryParams
      );
      if (response?.status === 200 && response.data) {
        const options = response.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1 ?? ""} (${t.code_1})`,
          code: `${t.code_1}; ${t.description_1}`,
        }));

        // Add NA option
        const naOption = {
          value: "na",
          label: "na",
          code: "na",
        };

        setComponentCodeOptions([...options, naOption]);
      }
    } catch (err) {
      console.error("Error in Fetching Component Codes:", err);
      notify("Error fetching component codes", "error");
    }
  };
  // Fetch SC Claims codes from API using v4 service
  const getSCClaimsCodes = async () => {
    try {
      const response =
        await dtrackItServiceV4.getCertifiedOrganizationApprovedClaimsValuesLG();
      if (response?.data) {
        const options = response.data.map((t: any) => {
          const cleanDesc = (t.description_1 ?? "")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&amp;/g, "&");
          return {
            value: cleanDesc,
            label: cleanDesc,
            code: t.code_1,
          };
        });
        setScClaimsCodeOptions(options);
      }
    } catch (err) {
      console.error("Error in Fetching SC Claims Codes:", err);
      notify("Error fetching SC claims codes", "error");
    }
  };

  // Validate that every SC standard has at least one selected Product Claims Category.
  // Claim codes follow the convention "{STANDARD_CODE}{suffix}" (e.g. RMS001, GRS-CL01),
  // so we check that the claim code starts with the standard code.
  const isClaimForStandard = (claim: string, standard: string) => {
    if (!claim) return false;
    const option = scClaimsCodeOptions.find(o => o.value === claim || o.code === claim);
    const codeToCheck = option ? option.code : claim;

    if (standard === "MMS") {
      return codeToCheck.startsWith("Materials Matter");
    }
    return codeToCheck.startsWith(standard);
  };

  const areClaimsConsistent = (standards: string[], savedClaimsList: string[]) => {
    if (standards.length === 0) return true;
    
    // 1. Every active standard must have at least one saved claim
    const hasMissing = standards.some(
      (std) => !savedClaimsList.some((claim) => isClaimForStandard(claim, std))
    );
    if (hasMissing) return false;

    // 2. Every saved claim must belong to an active standard
    const hasExtra = savedClaimsList.some(
      (claim) => !standards.some((std) => isClaimForStandard(claim, std))
    );
    if (hasExtra) return false;

    return true;
  };

  const validateAllStandardsHaveClaim = (): { valid: boolean; missing: string[] } => {
    if (!scStandards.length) return { valid: true, missing: [] };
    const missing = scStandards.filter((standard) =>
      !claimsCategoryData.some((claim) => isClaimForStandard(claim, standard))
    );
    return { valid: missing.length === 0, missing };
  };

  // ── Helper: strip trailing "(CODE)" from option label ───────────────────
  const stripCodeSuffix = (label: string) => label.replace(/\s*\([^)]+\)\s*$/, '').trim();

  // ── Component table handlers ─────────────────────────────────────────────
  const handleComponentAddNewRow = () => {
    if (isReadOnlyBasedOnStatus) return;
    setComponentTableData(prev => [{ code: '', description: '' }, ...prev]);
    setComponentEditingRowIndex(0);
  };

  const handleComponentCellChange = (rowIndex: number, columnId: string, value: any) => {
    if (columnId !== 'code') return;
    const description = value === 'na' ? 'na'
      : (componentCodeOptions.find((o: any) => o.value === value)?.label?.replace(/\s*\([^)]+\)\s*$/, '').trim() ?? '');
    setComponentTableData(prev =>
      prev.map((row, i) => i === rowIndex ? { code: value, description } : row)
    );
  };

  const handleComponentSaveRow = async () => {
    const rowData = componentTableData[componentEditingRowIndex];
    if (!rowData?.code) {
      notify('Please select a component code', 'error');
      return;
    }
    // Duplicate check
    const isDuplicate = componentTableData.some((r, i) => i !== componentEditingRowIndex && r.code === rowData.code);
    if (isDuplicate) {
      notify('This component code is already added', 'error');
      return;
    }
    // 'na' mutual exclusivity
    const otherCodes = componentTableData.filter((_, i) => i !== componentEditingRowIndex).map(r => r.code).filter(Boolean);
    if (rowData.code === 'na' && otherCodes.length > 0) {
      notify('"na" cannot be selected with other component codes. Please remove other components first.', 'error');
      return;
    }
    if (rowData.code !== 'na' && otherCodes.includes('na')) {
      notify('Cannot add a component when "na" is already selected. Please remove "na" first.', 'error');
      return;
    }
    const newCodes = componentTableData.map(r => r.code).filter(Boolean);

    try {
      const queryParams = {
        scId: SC_ID,
        scProductComponentCode: newCodes.map((val: string) =>
          componentCodeOptions?.find((c: any) => c.value === val)?.code
        ),
      };
      const result: any = await executePost(API_ROUTES.POST_COMPONENT_DETAILS_V4, queryParams);
      if (result?.status === 200 || result?.status === 201) {
        setComponentData(newCodes);
        setSavedComponentData(newCodes);
        setComponentEditingRowIndex(-1);
        notify('Component saved successfully', 'success');
      } else {
        notify('Failed to save component', 'error');
      }
    } catch (error) {
      console.error('Error saving component:', error);
      notify('Error saving component', 'error');
    }
  };

  const handleComponentCancelEdit = () => {
    // Restore from saved componentData (discards the unsaved row)
    const rows: ComponentTableRow[] = componentData.map(code => ({
      code,
      description: code === 'na' ? 'na'
        : (componentCodeOptions.find((o: any) => o.value === code)?.label?.replace(/\s*\([^)]+\)\s*$/, '').trim() ?? ''),
    }));
    setComponentTableData(rows);
    setComponentEditingRowIndex(-1);
  };

  const handleComponentEditRow = (rowIndex: number) => {
    setComponentEditingRowIndex(rowIndex);
  };

  const handleComponentDeleteRow = (rowIndex: number) => {
    setComponentDeletingRowIndex(rowIndex);
    setComponentDeleteDialogOpen(true);
  };

  const handleComponentConfirmDelete = async () => {
    const rowToDelete = componentTableData[componentDeletingRowIndex];
    const newCodes = componentData.filter(c => c !== rowToDelete.code);
    try {
      const queryParams = {
        scId: SC_ID,
        scProductComponentCode: newCodes.map((val: string) =>
          componentCodeOptions?.find((c: any) => c.value === val)?.code
        ),
      };
      const result: any = await executePost(API_ROUTES.POST_COMPONENT_DETAILS_V4, queryParams);
      if (result?.status === 200 || result?.status === 201) {
        setComponentData(newCodes);
        setSavedComponentData(newCodes);
        notify('Component deleted successfully', 'success');
      } else {
        notify('Failed to delete component', 'error');
      }
    } catch (error) {
      notify('Error deleting component', 'error');
    } finally {
      setComponentDeleteDialogOpen(false);
      setComponentDeletingRowIndex(-1);
    }
  };

  // ── Claims table handlers ────────────────────────────────────────────────
  const handleClaimsAddNewRow = () => {
    if (isReadOnlyBasedOnStatus) return;
    setClaimsTableData(prev => [{
      claimKey: '', scProductClaimsCategory: '', certifiedContent: '', blendingAllowed: '', mixingAllowed: ''
    }, ...prev]);
    setClaimsEditingRowIndex(0);
  };

  const handleClaimsCellChange = (rowIndex: number, columnId: string, value: any) => {
    if (columnId !== 'claimKey') return;
    const mapping = (scClaimsMapping as any)[value];
    setClaimsTableData(prev =>
      prev.map((row, i) => i === rowIndex ? {
        claimKey: value,
        scProductClaimsCategory: mapping?.scProductClaimsCategory ?? value,
        certifiedContent: mapping?.certifiedContent ?? '',
        blendingAllowed: mapping?.blendingAllowed ?? '',
        mixingAllowed: mapping?.mixingAllowed ?? '',
      } : row)
    );
  };

  const handleClaimsSaveRow = async () => {
    const rowData = claimsTableData[claimsEditingRowIndex];
    if (!rowData?.claimKey) {
      notify('Please select a claims category', 'error');
      return;
    }
    const isDuplicate = claimsTableData.some((r, i) => i !== claimsEditingRowIndex && r.claimKey === rowData.claimKey);
    if (isDuplicate) {
      notify('This claims category is already added', 'error');
      return;
    }
    const newKeys = claimsTableData.map(r => r.claimKey).filter(Boolean);

    try {
      const claimsParams = { scId: SC_ID, scProductClaimsCategory: newKeys };
      const result: any = await executePost(API_ROUTES.POST_PRODUCT_CLAIMS_DETAILS_V4, claimsParams);
      if (result?.status === 200 || result?.status === 201) {
        setClaimsCategoryData(newKeys);
        setSavedClaims(newKeys);
        if (SC_ID) scCacheService.setClaims(SC_ID, newKeys);
        setClaimsEditingRowIndex(-1);
        notify('Claims category saved successfully', 'success');
      } else {
        notify('Failed to save claims category', 'error');
      }
    } catch (error) {
      console.error('Error saving claims category:', error);
      notify('Error saving claims category', 'error');
    }
  };

  const handleClaimsCancelEdit = () => {
    const rows: ClaimsTableRow[] = claimsCategoryData.map(key => {
      const mapping = (scClaimsMapping as any)[key];
      return {
        claimKey: key,
        scProductClaimsCategory: mapping?.scProductClaimsCategory ?? key,
        certifiedContent: mapping?.certifiedContent ?? '',
        blendingAllowed: mapping?.blendingAllowed ?? '',
        mixingAllowed: mapping?.mixingAllowed ?? '',
      };
    });
    setClaimsTableData(rows);
    setClaimsEditingRowIndex(-1);
  };

  const handleClaimsEditRow = (rowIndex: number) => {
    setClaimsEditingRowIndex(rowIndex);
  };

  const handleClaimsDeleteRow = (rowIndex: number) => {
    setClaimsDeletingRowIndex(rowIndex);
    setClaimsDeleteDialogOpen(true);
  };

  const handleClaimsConfirmDelete = async () => {
    const rowToDelete = claimsTableData[claimsDeletingRowIndex];
    const newKeys = claimsCategoryData.filter(k => k !== rowToDelete.claimKey);
    try {
      const claimsParams = { scId: SC_ID, scProductClaimsCategory: newKeys };
      const result: any = await executePost(API_ROUTES.POST_PRODUCT_CLAIMS_DETAILS_V4, claimsParams);
      if (result?.status === 200 || result?.status === 201) {
        setClaimsCategoryData(newKeys);
        setSavedClaims(newKeys);
        if (SC_ID) scCacheService.setClaims(SC_ID, newKeys);
        notify('Claims category deleted successfully', 'success');
      } else {
        notify('Failed to delete claims category', 'error');
      }
    } catch (error) {
      notify('Error deleting claims category', 'error');
    } finally {
      setClaimsDeleteDialogOpen(false);
      setClaimsDeletingRowIndex(-1);
    }
  };

  // Button handlers following facility-product-details pattern
  const handleSCSave = async () => {
    try {
      if (!componentData || _.isEmpty(componentData)) {
        notify("Please add component information details", "error");
        return;
      }
      if (!claimsCategoryData || _.isEmpty(claimsCategoryData)) {
        notify("Please add SC Claims information details", "error");
        return;
      }

      // Validate: at least one claim must be selected for EACH standard
      const missingStandards = scStandards.filter(
        (standard) => !claimsCategoryData.some((claim) => isClaimForStandard(claim, standard))
      );
      if (missingStandards.length > 0) {
        notify(
          `Please select at least one claims category for standard(s): ${missingStandards.join(", ")}`,
          "error"
        );
        return;
      }

      // Save component details
      const componentQueryParams = {
        scId: SC_ID,
        scProductComponentCode: componentData.map((val: string) =>
          componentCodeOptions?.find((c: any) => c.value === val)?.code
        ),
      };
      await executePost(API_ROUTES.POST_COMPONENT_DETAILS_V4, componentQueryParams);

      // Save claims categories
      const claimsParams = {
        scId: SC_ID,
        scProductClaimsCategory: claimsCategoryData,
      };
      await executePost(API_ROUTES.POST_PRODUCT_CLAIMS_DETAILS_V4, claimsParams);

      // Save scope certificate data
      const params = {
        scId: SC_ID,
        scCblicensingCode: cbLicensingCode,
      };
      const result: any = await executePost(API_ROUTES.SAVE_SC_V4, params);
      if (result.status === 200) {
        notify("Saved Successfully", "success");
        // Bust the facility list cache so the next submit pre-check always reads fresh data.
        useScStore.getState().clearFacilityListCache();
        // Surgical update: write the just-saved claims to Zustand (L1) and localStorage (L2).
        // Next page load will serve these from localStorage — 0 API calls needed.
        setSavedClaims(claimsCategoryData);
        setSavedComponentData(componentData);
        if (SC_ID) scCacheService.setClaims(SC_ID, claimsCategoryData);
        setIsSubmitted(false);
      } else {
        notify("Unable to Save", "error");
      }
    } catch (error) {
      console.error("Error saving Component & Claims Category Details:", error);
      notify("Error saving Component & Claims Category Details", "error");
    }
  };

  const handleSCSubmit = async () => {
    try {
      if (!componentData || _.isEmpty(componentData)) {
        notify("Please add component information details", "error");
        return;
      }
      if (!claimsCategoryData || _.isEmpty(claimsCategoryData)) {
        notify("Please select at least one SC Product Claims Category", "error");
        return;
      }

      // Validate: at least one claim must be selected for EACH standard
      const missingClaimStandards = scStandards.filter(
        (standard) => !claimsCategoryData.some((claim) => isClaimForStandard(claim, standard))
      );
      if (missingClaimStandards.length > 0) {
        notify(
          `Please select at least one claims category for standard(s): ${missingClaimStandards.join(", ")}`,
          "error"
        );
        return;
      }

      // Pre-submit: validate facility standards AND detect PR0035 in one API call.
      // Always fetch fresh so we validate against the latest saved data.
      let hasPR0035FromFacilities = false;
      if (SC_ID) {
        try {
          useScStore.getState().clearFacilityListCache();
          const facilityListResp = await executeGet(API_ROUTES.GET_FACILITY_LIST_DETAILS_V4, { scId: SC_ID });

          // Use the same parsing as the original mismatch check — proven to work with
          // the actual API response format. The list endpoint returns:
          //   { data: { response: { facilityDetails: [...] } } }
          const facilities: any[] = facilityListResp?.data?.response?.facilityDetails || [];
          useScStore.getState().setFacilityListCache(facilityListResp?.data?.response);

          // ── Standard mismatch check ───────────────────────────────────────────
          if (scStandards.length > 0) {
            const standardMismatches: string[] = [];
            for (const facility of facilities) {
              const facilityName = facility.facilityName || "Unknown Facility";
              const facilityTeId = facility.facilityTeId || facility.facilityId || "";
              // facilityStandard may come as plain strings ["RWS"] or objects [{scStandard:"RWS"}].
              // Normalize to plain strings to make the includes() comparison reliable.
              const rawStds = Array.isArray(facility.facilityStandard)
                ? facility.facilityStandard
                : [facility.facilityStandard].filter(Boolean);
              const facilityStds: string[] = rawStds.map((s: any) =>
                typeof s === 'string' ? s : (s?.scStandard || '')
              ).filter(Boolean);

              const missing = scStandards.filter((s: string) => !facilityStds.includes(s));
              const extra = facilityStds.filter((s: string) => !scStandards.includes(s));
              if (missing.length > 0 || extra.length > 0) {
                const issues: string[] = [];
                if (missing.length > 0) issues.push(`missing standard(s): ${missing.join(", ")}`);
                if (extra.length > 0) issues.push(`withdrawn standard(s): ${extra.join(", ")}`);
                standardMismatches.push(
                  `'${facilityName}' (TE-ID: ${facilityTeId}) has ${issues.join(" and ")}`
                );
              }
            }
            if (standardMismatches.length > 0) {
              notify(
                `Facility standard mismatch — please go to Facility Information and update: ${standardMismatches.join(" | ")}`,
                "error"
              );
              return;
            }
          }

          // ── PR0035 detection from facility list ───────────────────────────────
          // The facilityProcessDetails field is returned by the list endpoint.
          // Also check facilityProcessInformation as a fallback (single-facility endpoint name).
          hasPR0035FromFacilities = facilities.some((f: any) => {
            const processList = f?.facilityProcessDetails || f?.facilityProcessInformation || [];
            return Array.isArray(processList) && processList.some(
              (p: any) => p?.facilityProcessCategoryCode === 'PR0035'
            );
          });
        } catch (facilityCheckErr) {
          console.warn("Pre-submit facility check failed (non-blocking):", facilityCheckErr);
        }
      }

      // ── PR0035 cross-tab pre-validation ───────────────────────────────────────
      // Use the freshly-computed hasPR0035FromFacilities (from the fetch above).
      // Fall back to the Zustand hasPR0035 only if the facility fetch returned nothing.
      const pr0035Active = hasPR0035FromFacilities || hasPR0035;
      if (pr0035Active) {
        try {
          const scDetailsResp = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
          const rawSCData = scDetailsResp?.data;

          const brandNames: string[] = rawSCData?.brandName || [];
          const approvedClaims: string = rawSCData?.certifiedOrganizationApprovedClaims || '';

          // Collect ALL errors first, then navigate once — so the user sees both
          // errors highlighted at once instead of fixing one at a time.
          const pr0035Errors: string[] = [];
          if (!brandNames || brandNames.length === 0) {
            pr0035Errors.push('brandName: Brand Name is required when process code PR0035 is present');
          }
          if (!approvedClaims || approvedClaims.trim() === '' || approvedClaims.trim().toUpperCase() === 'NONE') {
            pr0035Errors.push('certifiedOrganizationApprovedClaims: Certified Organization Approved Claims is required and cannot be "NONE" when process code PR0035 is present');
          }

          if (pr0035Errors.length > 0) {
            useScStore.getState().clearBackendValidationErrors();
            useScStore.getState().setBackendValidationErrors('scope-certificate-details', pr0035Errors);
            // Show one notification per missing field
            if (brandNames.length === 0) {
              notify('Brand Name is required when process code PR0035 is present.', 'error');
            }
            if (!approvedClaims || approvedClaims.trim() === '' || approvedClaims.trim().toUpperCase() === 'NONE') {
              notify('Certified Organization Approved Claims is required and cannot be "NONE" when process code PR0035 is present.', 'error');
            }
            navigate(`/v4/sc/${formtype}/scope-certificate-details/${SC_ID}`);
            return;
          }
        } catch (pr0035Err) {
          console.warn('[PR0035 pre-validation] SC details fetch failed — letting backend validate:', pr0035Err);
        }
      }

      const params = {
        scId: SC_ID,
      };
      // Clear previous validation errors
      useScStore.getState().clearBackendValidationErrors();

      const result: any = await executePost(API_ROUTES.SUBMIT_SC_V4, params);

      if (!result) return;
      
      if (result.status === 200) {
        notify("Submitted Successfully", "success");
        // ── Post-submit cache purge ────────────────────────────────────────────
        // Wipe all L1 Zustand transient state AND the L2 localStorage cache for
        // this SC so the next session (new SC or editing a different SC) always
        // starts completely clean. This is the definitive end of this SC session.
        useScStore.getState().clearAllTransientState();
        useScStore.getState().setPersistedScId(null);
        if (SC_ID) scCacheService.clearAll(SC_ID);
        setIsSubmitted(true);
      } else if (result.status === 422 && result.data) {
        // Backend Validation Error (422) handling
        const rawDetail = result.data.detail || result.data.error;
        const mappedErrors: Record<string, string[]> = {};

        // ── Handle plain-string detail (e.g. PR0035 custom backend errors) ──────
        // Pydantic errors return an array; custom errors return a plain string.
        if (typeof rawDetail === 'string') {
          const msgLower = rawDetail.toLowerCase();
          let targetTab = 'component-details'; // default: stay here
          if (msgLower.includes('facility')) {
            targetTab = 'facility-details';
          } else if (
            msgLower.includes('brandname') || msgLower.includes('brand name') ||
            msgLower.includes('certifiedorganization') || msgLower.includes('approved claims') ||
            msgLower.includes('pr0035')
          ) {
            targetTab = 'scope-certificate-details';
          }
          useScStore.getState().setBackendValidationErrors(targetTab, [rawDetail]);
          notify(rawDetail, 'error');
          if (targetTab !== 'component-details') {
            navigate(`/v4/sc/${formtype}/${targetTab}/${SC_ID}`);
          }
          return;
        }

        // ── Handle array detail (can be array of objects/standard Pydantic or array of custom error strings) ───
        const errors = Array.isArray(rawDetail) ? rawDetail : [];

        // Map backend API field names → tab IDs.
        // Keys are ALL LOWERCASE so the lookup below is case-insensitive.
        // The backend can return fields in any casing (e.g. 'BrandName', 'brandName', 'brand_name').
        const FIELD_TAB_MAP: Record<string, string> = {
          scstandardinformation: "scope-certificate-details",
          scopecertificateno: "scope-certificate-details",
          brandname: "scope-certificate-details",
          certifiedorganizationapprovedclaims: "scope-certificate-details",
          scnumberoffarms: "scope-certificate-details",
          isfarmsc: "scope-certificate-details",
          scopecertificatedates: "scope-certificate-details",
          facilitydetails: "facility-details",
          certifiedorganizationdetails: "certified-organization-details",
          scproductcategoryinformation: "product-category-information",
          scproductdetailinformation: "product-detail-information",
          scrawmaterialdetails: "raw-material-details",
          scproductcomponentcode: "component-details",
          scproductclaimscategory: "component-details",
        };

        let firstErrorTab: string | null = null;

        errors.forEach((errObj: any) => {
          if (typeof errObj === 'string') {
            // It's a plain string error from custom backend validations (like result.data.error)
            const msgLower = errObj.toLowerCase();
            let targetTab = 'component-details';
            if (msgLower.includes('facility')) {
              targetTab = 'facility-details';
            } else if (
              msgLower.includes('brandname') || msgLower.includes('brand name') ||
              msgLower.includes('certifiedorganization') || msgLower.includes('approved claims') ||
              msgLower.includes('pr0035') || msgLower.includes('farm') ||
              msgLower.includes('standard') || msgLower.includes('dates')
            ) {
              targetTab = 'scope-certificate-details';
            } else if (msgLower.includes('certified organization details') || msgLower.includes('certifiedorganizationdetails')) {
              targetTab = 'certified-organization-details';
            } else if (msgLower.includes('product category') || msgLower.includes('productcategory')) {
              targetTab = 'product-category-information';
            } else if (msgLower.includes('product detail') || msgLower.includes('productdetail')) {
              targetTab = 'product-detail-information';
            } else if (msgLower.includes('raw material') || msgLower.includes('rawmaterial')) {
              targetTab = 'raw-material-details';
            }

            if (!mappedErrors[targetTab]) {
              mappedErrors[targetTab] = [];
            }
            mappedErrors[targetTab].push(errObj);

            if (!firstErrorTab) {
              firstErrorTab = targetTab;
            }
          } else {
            // Standard Pydantic validation error object
            let rawField = errObj.loc ? errObj.loc[errObj.loc.length - 1] : "";
            const fieldNormalized = String(rawField).toLowerCase().replace(/_/g, '');
            const message = errObj.msg || "Validation error";

            let targetTab = FIELD_TAB_MAP[fieldNormalized] || null;
            if (!targetTab) {
              const msgLower = message.toLowerCase();
              if (msgLower.includes('facility')) {
                targetTab = 'facility-details';
              } else if (msgLower.includes('brandname') || msgLower.includes('brand name') ||
                  msgLower.includes('certifiedorganization') || msgLower.includes('pr0035')) {
                targetTab = 'scope-certificate-details';
                rawField = rawField || 'brandName';
              } else {
                targetTab = "component-details";
              }
            }

            if (!mappedErrors[targetTab]) {
              mappedErrors[targetTab] = [];
            }
            mappedErrors[targetTab].push(`${rawField}: ${message}`);

            if (!firstErrorTab) {
              firstErrorTab = targetTab;
            }
          }
        });

        // Save mapped errors to Zustand store
        Object.entries(mappedErrors).forEach(([tabId, messages]) => {
          useScStore.getState().setBackendValidationErrors(tabId, messages);
        });

        notify("Validation failed. Please correct the highlighted errors.", "error");

        // Navigate to the first tab that has an error
        if (firstErrorTab) {
          navigate(`/v4/sc/${formtype}/${firstErrorTab}/${SC_ID}`);
        }
      } else {
        const errMsg = result.message || "Unable to Submit";
        notify(errMsg, "error");
      }
    } catch (error) {
      console.error(
        "Error submitting Component & Claims Category Details:",
        error
      );
      notify("Error submitting Component & Claims Category Details", "error");
    }
  };


  const togglePreviewDrawer = () => setPreviewOpen((prev) => !prev);

  // Filter claim codes based on selected standards
  const filteredScClaimsCodeOptions = useMemo(() => {
    if (!scStandards.length || !scClaimsCodeOptions.length) {
      return scClaimsCodeOptions;
    }

    return scClaimsCodeOptions.filter((option: any) => {
      // Check if the claim code starts with any of the standard codes
      return scStandards.some((standard: string) => 
        isClaimForStandard(option.code, standard)
      );
    });
  }, [scStandards, scClaimsCodeOptions]);

  // When the available claim options change (because standards changed),
  // silently clear any selected claim that is no longer valid.
  // The missing-claims banner below will guide the user on what to re-select.
  useEffect(() => {
    if (filteredScClaimsCodeOptions.length === 0) return; // options not loaded yet
    const validCodes = new Set(filteredScClaimsCodeOptions.map((o: any) => o.value));
    setClaimsCategoryData(prev => prev.filter(c => validCodes.has(c)));
  }, [filteredScClaimsCodeOptions]);

  // Signal to the parent stepper whether this tab has active errors.
  // NO cleanup — error persists in Zustand until user actually fixes missing claims.
  // Cleared in full when parent stepper unmounts (SC session ends / SC_ID changes).
  useEffect(() => {
    const missingClaimsStandards = scStandards.filter(
      (std: string) => !claimsCategoryData.some((claim: string) => isClaimForStandard(claim, std))
    );
    const hasExtraClaims = claimsCategoryData.some(
      (claim: string) => !scStandards.some((std: string) => isClaimForStandard(claim, std))
    );
    setTabError('component-details', missingClaimsStandards.length > 0 || hasExtraClaims);
  }, [claimsCategoryData, scStandards]);

  const handleDownload = async () => {
    setIsDownloading(true);
    const options: DownloadDocxOptions = {
      apiUrl: API_ROUTES.GET_WORD_DOC_V4,
      params: {
        scId: SC_ID ?? "",
        cbCode: cbLicensingCode,
      },
      headers: {
        // Optional headers if needed
      },
    };

    try {
      notify("Starting Download", "info");
      await downloadDocx(options);
      notify("File Downloaded", "success");
      setIsDownloading(false);
    } catch (error) {
      notify("Failed to download DOCX", "error");
      setIsDownloading(false);
    }
  };

  // Form configuration for claims categories — kept for save/submit path
  console.log(isReadOnlyBasedOnStatus, "isReadOnlyBasedOnStatus");

  const isApiSC = scData?.submittedFromUi === "No" || scData?.submittedFromUi === "no" || scData?.submittedFromUi === false || scData?.scopeCertificateDetails?.submittedFromUi === "No" || scData?.scopeCertificateDetails?.submittedFromUi === "no" || scData?.scopeCertificateDetails?.submittedFromUi === false;

  const isAlreadySubmitted = scData?.scopeCertificateDates?.scStatus && 
    ["I", "V", "E", "S", "W", "Issued", "Valid", "Expired", "Suspended", "Withdrawn"].includes(scData.scopeCertificateDates.scStatus);

  const isSubmittedSC = hasSubmitSC || isAlreadySubmitted || isSubmitted;

  const isDownloadDisabled = isDownloading || !isSubmittedSC || isApiSC;

  const downloadTooltip = isApiSC
    ? "Word document generation is restricted and unavailable for API-submitted data"
    : !isSubmittedSC
    ? "Word document is only available for download after successful submission"
    : "Click to Download";

  // ── Column definitions ───────────────────────────────────────────────────
  const componentColumns: EditableColumnDef<ComponentTableRow>[] = [
    {
      accessorKey: 'code',
      header: 'SC Product Component Code',
      Header: (
        <span>
          <i style={{ color: 'red' }}>* </i>SC Product Component Code
        </span>
      ),
      size: 200,
      minSize: 160,
      maxSize: 260,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="code"
          label=""
          value={value}
          placeholder="Select SC Product Component Code"
          options={componentCodeOptions}
          enableSearch={true}
          size="small"
          showCodeOnlyWhenSelected={true}
          isOptionDisabled={(option: any) => {
            const otherCodes = componentTableData
              .filter((_, i) => i !== componentEditingRowIndex)
              .map((r) => r.code)
              .filter(Boolean);
            if (option.value === 'na' && otherCodes.length > 0) return true;
            if (option.value !== 'na' && otherCodes.includes('na')) return true;
            return false;
          }}
          onChange={(e: any) => onChange(e.target.value)}
        />
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      Header: <span>Description</span>,
      size: 220,
      minSize: 160,
      maxSize: 320,
      grow: true,
    },
  ];

  const claimsColumns: EditableColumnDef<ClaimsTableRow>[] = [
    {
      accessorKey: 'claimKey',
      header: 'SC Product Claims Category',
      Header: (
        <span>
          <i style={{ color: 'red' }}>* </i>SC Product Claims Category
        </span>
      ),
      size: 195,
      minSize: 140,
      maxSize: 195,
      grow: false,
      muiTableBodyCellProps: {
        sx: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4, verticalAlign: 'top' },
      },
      // In display mode, show only the category name (not the full key)
      Cell: ({ row }: any) => <span>{row.original.scProductClaimsCategory || ''}</span>,
      renderEditCell: ({ value, onChange }) => {
        const alreadySelected = new Set(
          claimsTableData.filter((_, i) => i !== claimsEditingRowIndex).map((r) => r.claimKey)
        );
        return (
          <CustomSelect
            name="claimKey"
            label=""
            value={value}
            placeholder="Select SC Product Claims Category"
            options={filteredScClaimsCodeOptions.map((opt: any) => ({
              value: opt.value,
              label: opt.value,
            }))}
            enableSearch={true}
            size="small"
            isOptionDisabled={(option: any) => alreadySelected.has(option.value)}
            onChange={(e: any) => onChange(e.target.value)}
          />
        );
      },
    },
    {
      accessorKey: 'certifiedContent',
      header: 'Certified Content',
      Header: <span>Certified Content</span>,
      size: 140,
      minSize: 95,
      maxSize: 140,
      grow: false,
      muiTableBodyCellProps: {
        sx: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4, verticalAlign: 'top' },
      },
    },
    {
      accessorKey: 'blendingAllowed',
      header: 'Blending Allowed',
      Header: <span>Blending Allowed</span>,
      size: 145,
      minSize: 140,
      maxSize: 145,
      grow: false,
      muiTableBodyCellProps: {
        sx: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4, verticalAlign: 'top' },
      },
    },
    {
      accessorKey: 'mixingAllowed',
      header: 'Mixing Allowed',
      Header: <span>Mixing Allowed</span>,
      size: 150,
      minSize: 135,
      maxSize: 145,
      grow: true,
      muiTableBodyCellProps: {
        sx: { whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4, verticalAlign: 'top' },
      },
    },
  ];

  return (
    <>
      {isInitialLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {/* Compact missing-claims bar + unsaved-changes notice */}
          {(() => {
            if (isSubmittedSC) return null;
            const missingClaimsStandards = scStandards.filter(
              std => !claimsCategoryData.some(claim => isClaimForStandard(claim, std))
            );
            if (missingClaimsStandards.length === 0 && !hasUnsavedChanges) return null;
            return (
              <Box mb={1} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
                bgcolor: missingClaimsStandards.length > 0 ? "#fff0f0" : "#fff8e1",
                border: `1px solid ${missingClaimsStandards.length > 0 ? "#e53935" : "#f9a825"}`,
                borderRadius: "4px", px: 1.5, py: 0.75 }}>

                {missingClaimsStandards.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ color: "#b71c1c", fontWeight: 700, mr: 0.5 }}>
                      ⛔ Missing claims category for:
                    </Typography>
                    {missingClaimsStandards.map((std, i) => (
                      <Tooltip key={i} arrow title={`No claims category selected for standard "${std}" — select one from the SC Product Claims Category dropdown`}>
                        <Chip
                          label={std}
                          size="small"
                          sx={{ bgcolor: "#ffcdd2", color: "#b71c1c", fontWeight: 700,
                            fontSize: "0.7rem", height: 20, cursor: "help" }}
                        />
                      </Tooltip>
                    ))}
                    <Typography variant="caption" sx={{ color: "#7f0000" }}>
                      — select a claims category for each highlighted standard
                    </Typography>
                  </>
                )}

                {hasUnsavedChanges && (
                  <Typography variant="caption" sx={{
                    color: "#7a4100", fontWeight: 600,
                    ml: missingClaimsStandards.length > 0 ? 1 : 0,
                    borderLeft: missingClaimsStandards.length > 0 ? "1px solid #f9a825" : "none",
                    pl: missingClaimsStandards.length > 0 ? 1 : 0,
                  }}>
                    ⚠ Unsaved changes — click <strong>Save</strong> before submitting
                  </Typography>
                )}
              </Box>
            );
          })()}

          {/* Side-by-Side Responsive Grid Container */}
          <Grid container spacing={3} sx={{ width: "100%", mb: 2, boxSizing: "border-box" }}>
            {/* Left Side: Component Information Section */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} width="100%">
                <Typography variant="subtitle1" fontWeight={600}>
                  Component Information
                </Typography>
                <StyledButton
                  onClick={handleComponentAddNewRow}
                  disabled={isReadOnlyBasedOnStatus || componentEditingRowIndex !== -1}
                >
                  Add Component
                </StyledButton>
              </Box>
              <EditableTable
                data={componentTableData}
                columns={componentColumns}
                onSaveRow={handleComponentSaveRow}
                onCellChange={handleComponentCellChange}
                editingRowIndex={componentEditingRowIndex}
                onEditRow={handleComponentEditRow}
                onCancelEdit={handleComponentCancelEdit}
                onDeleteRow={handleComponentDeleteRow}
                enableEditing={!isReadOnlyBasedOnStatus}
                isSaving={isSaving}
                isV4={true}
              />
            </Grid>

            {/* Right Side: SC Product Claims Category Section */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} width="100%">
                <Typography variant="subtitle1" fontWeight={600}>
                  SC Product Claims Category
                </Typography>
                <StyledButton
                  onClick={handleClaimsAddNewRow}
                  disabled={isReadOnlyBasedOnStatus || claimsEditingRowIndex !== -1}
                >
                  Add Claims Category
                </StyledButton>
              </Box>
              <EditableTable
                data={claimsTableData}
                columns={claimsColumns}
                onSaveRow={handleClaimsSaveRow}
                onCellChange={handleClaimsCellChange}
                editingRowIndex={claimsEditingRowIndex}
                onEditRow={handleClaimsEditRow}
                onCancelEdit={handleClaimsCancelEdit}
                onDeleteRow={handleClaimsDeleteRow}
                enableEditing={!isReadOnlyBasedOnStatus}
                isSaving={isSaving}
                isV4={true}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: "0.5rem",
              width: "100%",
            }}
          >
            <NavLink to="/v4/sc-dashboard">
              <InnerButton>Home</InnerButton>
            </NavLink>

            <InnerButton
              onClick={handleSCSave}
              // disabled={isReadOnlyBasedOnStatus}
            >
              Save
            </InnerButton>
            {(componentData?.length > 0 || claimsCategoryData?.length > 0) && (
              <>
              {(() => {
                  const missingClaimsStandards = scStandards.filter(
                    std => !claimsCategoryData.some(claim => isClaimForStandard(claim, std))
                  );
                  const isSubmitDisabled = hasUnsavedChanges || missingClaimsStandards.length > 0 || !areClaimsConsistent(scStandards, savedClaims);
                  const submitTooltip = hasUnsavedChanges
                    ? "Please save your changes before submitting"
                    : (missingClaimsStandards.length > 0 || !areClaimsConsistent(scStandards, savedClaims))
                    ? "Please save claims data for all active standards before submitting"
                    : "";
                  return (
                    <Tooltip title={submitTooltip} disableHoverListener={!isSubmitDisabled}>
                      <span>
                        <InnerButton
                          onClick={handleSCSubmit}
                          disabled={isSubmitDisabled}
                          sx={{ opacity: isSubmitDisabled ? 0.5 : 1 }}
                        >
                          Submit
                        </InnerButton>
                      </span>
                    </Tooltip>
                  );
                })()}

                <Tooltip title={"Click to Preview"}>
                  <span>
                    <InnerButton onClick={togglePreviewDrawer}>
                      Preview
                    </InnerButton>
                  </span>
                </Tooltip>

                <Tooltip title={downloadTooltip}>
                  <span>
                    <InnerButton
                      onClick={handleDownload}
                      disabled={isDownloadDisabled}
                      sx={{
                        position: "relative",
                        opacity: isDownloadDisabled ? 0.5 : 1,
                        "& .loading-indicator": {
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                        },
                      }}
                    >
                      {isDownloading ? (
                        <Box
                          className="loading-indicator"
                          display="flex"
                          alignItems="center"
                        >
                          <CircularProgress size={20} color="inherit" />
                        </Box>
                      ) : (
                        "Download Word"
                      )}
                    </InnerButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>

          {/* Preview Modal */}
          {SC_ID && (
            <ViewerModalPopup
              open={previewOpen}
              onClose={togglePreviewDrawer}
              title="Preview"
              onDownload={() => {
                if (SC_ID) {
                  downloadPDF(
                    API_ROUTES.GET_PREVIEW_SC_V4,
                    { scId: SC_ID },
                    scData?.scNo ? `${scData.scNo}.pdf` : `draft_sc_${SC_ID}.pdf`
                  );
                }
              }}
            >
              <div style={{ width: "100%", height: "100vh" }}>
                <PdfViewer
                  apiUrl={API_ROUTES.GET_PREVIEW_SC_V4}
                  params={{ scId: SC_ID }}
                  fileName={scData?.scNo || (SC_ID ? `draft_sc_${SC_ID}` : "scope_certificate")}
                />
              </div>
            </ViewerModalPopup>
          )}

          {/* Component delete confirmation */}
          <AlertPopup
            open={componentDeleteDialogOpen}
            title="Confirm Delete"
            message="Are you sure you want to delete this component? This action cannot be undone."
            onClose={() => setComponentDeleteDialogOpen(false)}
            onConfirm={handleComponentConfirmDelete}
            confirmText="Delete"
            cancelText="Cancel"
          />

          {/* Claims delete confirmation */}
          <AlertPopup
            open={claimsDeleteDialogOpen}
            title="Confirm Delete"
            message="Are you sure you want to delete this claims category? This action cannot be undone."
            onClose={() => setClaimsDeleteDialogOpen(false)}
            onConfirm={handleClaimsConfirmDelete}
            confirmText="Delete"
            cancelText="Cancel"
          />
        </>
      )}
    </>
  );
};

export default ComponentClaimsCategoryDetailsTab;
